import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  ListItemText,
  FormHelperText,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// functions to make api calls
import { getCaseById, updateCaseById } from "../../api/cases.js";

// non paginated table
import AgGridTable from "../../components/AgGridTable.jsx";

// column definition for table
import { caseUsersTable } from "../../config/grid-column-defs.js";

// custom hook for showing success or error messages on left bottom of screen
import useSnackbar from "../../hooks/useSnackbar.js";

// custom component to render something conditionally
import ConditionalRenderer from "../../components/ConditionalRenderer.jsx";
import useErrorHandler from "../../hooks/useErrorHandler.js";
import DynamicTabPanel from "../../components/DynamicTabPanel.jsx";
import { getUserListByCaseId } from "../../api/user.js";
import { addCaseUsers } from "../../api/caseUsers.js";
import DropdownEditableTable from "../../components/DropdownEditableTable.jsx";
import { Delete } from "@mui/icons-material";
import { deleteCaseById, resendRespondentInvitation } from "../../api/cases.js";
import CaseDocuments from "../documents/component/case-documents.jsx";
import CaseNegotiation from "../negotiation/component/case-negotiation.jsx";
import CaseWorkflow from "../../components/CaseWorkflow.jsx";
import Meetings from "../meeting/meetings-list.jsx";

// initial master data when the page is just loaded and api response is awaiting
const emptyCaseData = {
  description: "",
  title: "",
  start_date: "",
  end_date: "",
  status: "",
  type: "",
};

export default function NewCase() {
  const handleError = useErrorHandler();
  const snackbar = useSnackbar();

  // to grab the dynamic part of URL
  const params = useParams();
  // holds the global user state (data)
  const { data: user } = useSelector((state) => state.user);

  // master data state
  const [caseData, setCaseData] = useState(emptyCaseData);
  // detail data state
  const [caseUsers, setCaseUsers] = useState([]);

  const [isPrimary, setIsPrimary] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkList, setCheckList] = useState(true);

  // function to handle input change
  const onInputChange = ({ target }) => {
    setCaseData((prev) => ({ ...prev, [target.name]: target.value }));
  };

  // this function will run whenever the :id in url or token in user state changes
  useEffect(() => {
    if (!user.token) return;
    getCaseDetailById();
    // function responsible for fetching master-detail data
  }, [params.id, user.token]);

  async function getCaseDetailById() {
    getCaseById(user.token, params.id)
      .then(({ data }) => {
        // setting fetched master data
        setCaseData(data.case);
        // setting fetched detail data
        setCaseUsers(data.users);
        setIsPrimary(data.primary);
      })
      .catch((err) => {
        handleError(err);
        // in case error happens
        snackbar.showError();
      })
      .finally(() => {});
  }
  // staff can correct the intake details and re-send the respondent's invitation
  const isStaff = /^(admin|registrar)$/i.test(user.roles);
  const onResendInvitation = async () => {
    try {
      await resendRespondentInvitation(user.token, caseData.id);
      snackbar.showSuccess("Invitation sent");
      getCaseDetailById();
    } catch (err) {
      handleError(err);
    }
  };
  // when master data is edited
  const onUpdateFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateCaseById(
        user.token,
        caseData.id,
        getUpdatableCaseData(caseData)
      );
      snackbar.showSuccess("Case Updated");
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <>
      <CaseWorkflow caseId={params.id} onChanged={getCaseDetailById} />
      {/* <Box mb={2} display='flex' justifyContent='flex-end'>
        <Link to="/cases">
          <Button variant="contained" size="small" color="secondary">Cancel</Button>
        </Link>
      </Box> */}
      <Paper elevation={3} className="odr-card">
        <Box className="odr-card-header">
          <Typography className="heading-title">Case Details</Typography>
        </Box>

        <Box px={2} pb={2} component="form" onSubmit={onUpdateFormSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                value={caseData.title}
                label="Title"
                onChange={onInputChange}
                size="small"
                fullWidth
                variant="outlined"
                name="title"
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={8}>
              <TextField
                value={caseData.description}
                label="Description"
                onChange={onInputChange}
                size="small"
                fullWidth
                variant="outlined"
                name="description"
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth size="small" varaint="outlined">
                <InputLabel id="demo-simple-select-s">Status</InputLabel>
                <Select
                  variant="outlined"
                  labelId="demo-simple-select-label-s"
                  id="demo-simple-select-s"
                  name="status"
                  value={caseData.status}
                  label="Status"
                  onChange={onInputChange}
                >
                  <MenuItem value="INITIAL">Initial</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} md={6} lg={4}>
              <TextField value={caseData.status} label="Status" onChange={onInputChange}
                size='small' fullWidth variant="outlined" name="status" disabled
              />
            </Grid> */}
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                value={caseData.type}
                label="Type"
                onChange={onInputChange}
                size="small"
                fullWidth
                variant="outlined"
                name="type"
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  required
                  // minDateTime={parseISO(new Date().toISOString())}
                  value={parseISO(caseData.start_date)}
                  onChange={(newValue) => {
                    onInputChange({
                      target: {
                        name: "start_date",
                        value: newValue.toISOString(),
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      required
                      {...params}
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  required
                  minDate={parseISO(caseData.start_date)}
                  value={parseISO(caseData.end_date)}
                  onChange={(newValue) => {
                    onInputChange({
                      target: {
                        name: "end_date",
                        value: newValue.toISOString(),
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            {/* intake details captured when the case was filed; cases filed before this existed have none.
                Staff can correct them; the arbitration declaration is fixed. */}
            {caseData.respondent_name ? (
              <>
                {[
                  ["respondent_name", "Respondent", "text"],
                  ["respondent_email", "Respondent email", "email"],
                  ["respondent_phone", "Respondent phone", "tel"],
                  ["claim_amount", "Amount claimed ($)", "number"],
                ].map(([name, label, type]) => (
                  <Grid item xs={12} md={6} lg={4} key={name}>
                    <TextField
                      value={caseData[name] ?? ""}
                      name={name}
                      label={label}
                      type={type}
                      inputProps={type === "number" ? { min: 0.01, step: "0.01" } : undefined}
                      onChange={onInputChange}
                      size="small"
                      fullWidth
                      variant="outlined"
                      disabled={!isStaff}
                      required={isStaff}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    value={
                      caseData.arbitration_agreement
                        ? `Declared ${caseData.agreement_declared_at ? new Date(caseData.agreement_declared_at).toLocaleDateString() : ""}`.trim()
                        : "Not declared"
                    }
                    label="Agreement to arbitrate"
                    size="small"
                    fullWidth
                    variant="outlined"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={8}>
                  <Box className="flex flex-wrap items-center gap-3 min-h-[40px]">
                    <Typography className="text-[13px] text-[#6b7a90]">
                      {caseData.respondent_invited_at
                        ? `Respondent invited on ${new Date(caseData.respondent_invited_at).toLocaleDateString()}`
                        : "Respondent not invited yet"}
                    </Typography>
                    {isStaff ? (
                      <Button size="small" variant="outlined" onClick={onResendInvitation}>
                        {caseData.respondent_invited_at ? "Resend invitation" : "Send invitation"}
                      </Button>
                    ) : null}
                  </Box>
                </Grid>
              </>
            ) : null}
            <ConditionalRenderer
              show={/^admin|registrar|trial_user$/i.test(user.roles)}
            >
              <Grid item xs={12} md="auto" lg="auto">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  className="blue-button"
                  disabled={/^trial_user$/i.test(user.roles) ? true : false}
                >
                  Edit
                </Button>
              </Grid>
            </ConditionalRenderer>
          </Grid>
        </Box>
      </Paper>
      <br />
      <Paper className="odr-card">
        <DynamicTabPanel
          tabs={[
            {
              label: "Case Users",
              component: (
                <CaseUsersTable
                  refreshApi={getCaseDetailById}
                  checkList={checkList}
                  open={dialogOpen}
                  setOpen={setDialogOpen}
                  user={user}
                  caseUsers={caseUsers}
                  caseUsersTable={caseUsersTable}
                  isPrimary={isPrimary}
                />
              ),
            },
            {
              label: "Case Documents",
              // component: <Navigate to={`/documents/${params.id}`} />
              component: <CaseDocuments />,
            },
            {
              label: "Case Negotiations",
              // component: <Navigate to={`/negotiation/${params.id}`} />,
              component: <CaseNegotiation />,
            },
            {
              label: "Case Meetings",
              // component: <Navigate to={`/negotiation/${params.id}`} />,
              component: <Meetings caseId={params.id} />,
            },
          ]}
          setSelected={() => {}}
        />
      </Paper>
      <AddCaseUserDialog
        setCheck={setCheckList}
        open={dialogOpen}
        setOpen={setDialogOpen}
        token={user.token}
        caseId={params.id}
        refreshApi={getCaseDetailById}
      />

      {/* above this line  is master data and below this is details data*/}
      {/* <Paper>
        <Box px={2} py={1} display='flex' justifyContent='space-between'>
          <Typography>
            Case Users
          </Typography>
          <ConditionalRenderer show={/^admin$/i.test(user.roles)}>
            <Link to={`users`}>
              <Button size="small" variant="contained">Add Case User</Button>
            </Link>
          </ConditionalRenderer>
        </Box>
        <AgGridTable columns={caseUsersTable} rowData={caseUsers} />
      </Paper> */}
      {/* <Box py={1}>
        <Link to={`/documents/${params.id}`}>
          <Button variant="contained" >See Case Documents</Button>
        </Link>
      </Box> */}
    </>
  );
}

// function to filter out onlythe  data that is allowed to be updated
function getUpdatableCaseData(data) {
  return {
    title: data.title,
    description: data.description,
    start_date: data.start_date,
    end_date: data.end_date,
    status: data.status,
    // intake details only exist on cases filed with them
    ...(data.respondent_name
      ? {
          respondent_name: data.respondent_name,
          respondent_email: data.respondent_email,
          respondent_phone: data.respondent_phone,
          claim_amount: Number(data.claim_amount),
        }
      : {}),
  };
}

function CaseUsersTable({
  refreshApi,
  checkList,
  open,
  setOpen,
  user,
  caseUsersTable,
  caseUsers,
  isPrimary,
}) {
  const [caseUsersTableData, setCaseUsersTableData] = useState([
    ...caseUsersTable,
  ]);
  const snackbar = useSnackbar();
  const errorHandler = useErrorHandler();
  const dispatch = useDispatch();
  const dialogAction = useSelector((state) => state.dialog);
  useEffect(() => {
    if (!dialogAction.type) return;
    switch (dialogAction.type) {
      case "DELETE":
        dispatch({ type: "" });
        handleDelete(dialogAction.data.id);

        break;

      default:
        break;
    }
  }, [dialogAction]);
  // Function to delete a case user
  async function handleDelete(id) {
    try {
      const { data } = await deleteCaseById(user.token, id);
      await refreshApi();
      snackbar.showSuccess(data.msg);
    } catch (error) {
      errorHandler(error);
    }
  }

  // Check if the delete button exists in the table

  // If the delete button does not exist, add it to the table
  useEffect(() => {
    if (user?.roles) {
      const isAdminOrRegistrar = /^admin|registrar$/i.test(user.roles);
      setCaseUsersTableData((prevTable) => {
        if (isAdminOrRegistrar) {
          // If the user is an admin or registrar, include the "Action" column at the beginning of the array
          const newColumns = prevTable.filter(
            (col) => col.headerName !== "Action"
          );
          newColumns.unshift({
            headerName: "Action",
            field: "delete",
            sortable: false,
            filter: false,
            width: 100,
            cellRendererFramework: (params) => (
              <Delete
                variant="contained"
                color="secondary"
                className="red-button"
                onClick={() => {
                  dispatch({ type: "DELETE", data: params.data });
                }}
              >
                Delete
              </Delete>
            ),
          });
          return newColumns;
        } else {
          // If the user is not an admin or registrar, exclude the "Action" column
          return prevTable.filter((col) => col.headerName !== "Action");
        }
      });
    }
  }, [user?.roles]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <ConditionalRenderer
          show={
            /^admin|registrar|trial_user$/i.test(user.roles) ||
            /true/.test(isPrimary)
          }
        >
          <Button
            onClick={() => {
              if (checkList === true) {
                setOpen(true);
              } else {
                snackbar.showInfo("There is no user to add in case.");
              }
            }}
            size="large"
            variant="contained"
            className="blue-button"
          >
            Add Case User
          </Button>
        </ConditionalRenderer>
      </Box>
      {/* <Box display="flex" justifyContent="space-between">
        <ConditionalRenderer show={/true/.test(isPrimary)}>
          <Button
            onClick={() => {
              if (checkList === true) {
                setOpen(true);
              } else {
                snackbar.showInfo("There is no user to add in case.");
              }
            }}
            size="small"
            variant="contained"
          >
            Add Case User
          </Button>
        </ConditionalRenderer>
      </Box> */}
      <AgGridTable columns={caseUsersTableData} rowData={caseUsers} />
    </>
  );
}

function AddCaseUserDialog({
  setCheck,
  open = true,
  setOpen,
  token,
  caseId,
  refreshApi,
}) {
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [type, setType] = useState("");
  const [side, setSide] = useState("");
  const navigate = useNavigate();
  const [isPrimary, setIsPrimary] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const handleError = useErrorHandler();
  const snackbar = useSnackbar();
  const { data: user } = useSelector((state) => state.user);
  useEffect(() => {
    if (!token) return;
    getUserList();
  }, [token]);
  const handleClose = () => {
    setOpen(false);
  };

  async function getUserList() {
    try {
      const { data } = await getUserListByCaseId(token, caseId);
      setUsersList(data.data);
      if (!data.data.length) {
        setCheck(false);
      }
    } catch (error) {
      handleError(error);
      // console.log(error);
    }
  }
  async function handleFormSubmit(e) {
    e.preventDefault();
    try {
      await addCaseUsers(token, caseId, {
        users: formattedData(id, name, type, side, isPrimary),
      });
      snackbar.showSuccess("Users Added.");
      refreshApi();
      setName("");
      setId("");
      setType("");
      setSide("");
      setIsPrimary(false);
      // navigate(`/cases/${caseId}`, { replace: true });
    } catch (error) {
      handleError(error);
    } finally {
      setOpen(false);
    }
  }

  function onValueChange(data) {
    setName(data.name);
    setId(data.id);
    setType("PLAINTIFF");
    setSide("PLAINTIFF");
  }
  return (
    <Dialog
      open={open}
      //onClose={handleClose}
      onClose={() => {}}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle className="heading-title">Create</DialogTitle>
      <DialogContent>
        <Box component="form" pt={1} onSubmit={handleFormSubmit}>
          <Grid container spacing={1}>
            <Grid item md={6} xl={6}>
              <DropdownEditableTable
                caseId={caseId}
                onValueChange={onValueChange}
              />
              {/* <FormControl size="small" fullWidth>
                <InputLabel id="demo-multiple-checkbox-label">
                  {"Users"}
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  // id="demo-multiple-checkbox"

                  fullWidth
                  value={selectedUser}
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                    const selectedUserData = usersList.find(
                      (c) => c.id == e.target.value
                    );
                    setName(selectedUserData.name);
                    setId(selectedUserData.id);
                  }}
                >
                  {usersList.map((u, idx) => (
                    <MenuItem key={idx} value={u.id}>
                      <ListItemText primary={u.name} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{""}</FormHelperText>
              </FormControl> */}
            </Grid>
            <Grid item md={6} xl={6}>
              <TextField
                className="mt-3"
                name="name"
                size="small"
                fullWidth
                required
                onChange={(e) => {
                  setName(e.target.value);
                }}
                value={name}
                label="Name"
                disabled
              />
            </Grid>
            <Grid item md={6} xl={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={type}
                  onChange={({ target }) => {
                    setType(target.value);

                    if (target.value === "PLAINTIFF") {
                      setSide("PLAINTIFF");
                    } else if (target.value === "DEFENDANT") {
                      setSide("DEFENDANT");
                    } else {
                      setSide("DEFENDANT");
                    }
                  }}
                >
                  <MenuItem value="PLAINTIFF">Plaintiff</MenuItem>
                  <MenuItem value="DEFENDANT">Defendant</MenuItem>
                  <MenuItem value="ATTORNEY">Attorney</MenuItem>
                  <MenuItem value="TRANSLATOR">Translator</MenuItem>
                  <MenuItem value="SUPPORTER">Supporter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={12} xl={12}>
              <FormControl>
                <RadioGroup
                  row
                  value={side}
                  onChange={({ target }) => {
                    setSide(target.value);
                  }}
                >
                  <FormControlLabel
                    value="PLAINTIFF"
                    control={<Radio />}
                    label="Plaintiff"
                  />
                  <FormControlLabel
                    value="DEFENDANT"
                    control={<Radio />}
                    label="Defendant"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {type === "SUPPORTER" ||
            type === "ATTORNEY" ||
            type === "TRANSLATOR" ? (
              <></>
            ) : (
              <Grid item md={12} xl={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isPrimary}
                      onChange={({ target }) => {
                        setIsPrimary(target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label="Primary User"
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          className="black-border-button"
          size="large"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          className="blue-button"
          disabled={/^trial_user$/i.test(user.roles) ? true : false}
          onClick={handleFormSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function formattedData(id, name, type, side, isPrimary) {
  return [
    {
      id: id,
      name: name,
      isPrimary: isPrimary,
      from_plaintiff: side === "PLAINTIFF" ? true : false,
      type: type,
    },
  ];
}
